import { XIcon } from "@heroicons/react/outline";
import React from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "ui/components/primitives/drawer";
import { cn } from "ui/lib/utils";
import { IconButton } from "./icon-button";
import {
  type FocusReturn,
  captureOpener,
  registerFocusHost,
  returnFocus,
} from "./return-focus";
import { useSheetHistory } from "./sheet-history";

type BottomSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown in the sheet's header and read as its accessible name. */
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Pinned under the body, above the home indicator. */
  footer?: React.ReactNode;
  /** Open at full height (forms, search) instead of sizing to the content. */
  full?: boolean;
  /** Replaces the default title row, e.g. FormSheet's Cancel | Title | Save. */
  header?: React.ReactNode;
  /** Focus moves here on open; by default focus goes to the sheet itself. */
  initialFocus?: React.RefObject<HTMLElement>;
  /**
   * The `usePushedView` param whose value opens this sheet. The URL's entry
   * is then the sheet's, and it takes no history entry of its own.
   */
  urlView?: string;
  /**
   * For sheets a long-press opens under the finger: a pointer click counts
   * only after a press that began once the sheet was open, so the release
   * of the opening press can't choose a row. Keyboard clicks always count.
   */
  ignoreOpeningPress?: boolean;
  className?: string;
};

// Whether the last input before close was a pointer, so focus handed back on
// close shows a ring only after keyboard use.
let lastInputWasPointer = false;
let trackingInput = false;
const trackInput = () => {
  if (trackingInput || typeof document === "undefined") return;
  trackingInput = true;
  const pointer = () => (lastInputWasPointer = true);
  const keyboard = () => (lastInputWasPointer = false);
  document.addEventListener("pointerdown", pointer, true);
  document.addEventListener("keydown", keyboard, true);
};

/**
 * The phone's stand-in for menus, popovers, pickers and side panels: slides
 * up from the bottom, closes by swiping down, the 44px close button or Back.
 */
export const BottomSheet = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  full,
  header,
  initialFocus,
  urlView,
  ignoreOpeningPress,
  className,
}: BottomSheetProps) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  // Sheets open from state, not a Radix trigger, so Radix has nothing to
  // return focus to; remember what had it and give it back on every close.
  const returnFocusTo = React.useRef<FocusReturn | undefined>(undefined);

  const pressedSinceOpen = React.useRef(true);
  // Layout effect: armed before the browser can deliver the release.
  React.useLayoutEffect(() => {
    if (!open || !ignoreOpeningPress) return;
    pressedSinceOpen.current = false;
    const press = () => (pressedSinceOpen.current = true);
    document.addEventListener("pointerdown", press, true);
    return () => document.removeEventListener("pointerdown", press, true);
  }, [open, ignoreOpeningPress]);

  // Back closes the sheet rather than leaving the page.
  useSheetHistory(open && !urlView, () => onOpenChange(false));
  React.useEffect(trackInput, []);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
    >
      <DrawerContent
        ref={contentRef}
        tabIndex={-1}
        {...(description ? {} : { "aria-describedby": undefined })}
        onClickCapture={(event) => {
          // detail is 0 for a click from Enter or Space.
          if (!ignoreOpeningPress || pressedSinceOpen.current) return;
          if (event.detail === 0) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onCloseAutoFocus={(event) => {
          // An action the sheet started (a dialog, a pager) may own focus now.
          if (returnFocus(returnFocusTo.current, !lastInputWasPointer)) {
            event.preventDefault();
          }
        }}
        onOpenAutoFocus={(event) => {
          // Runs before focus moves in, so this is still the opener.
          returnFocusTo.current = captureOpener();
          registerFocusHost(contentRef.current, returnFocusTo.current);
          // Raising the keyboard uninvited hides half the sheet on a phone.
          event.preventDefault();
          (initialFocus?.current ?? contentRef.current)?.focus();
        }}
        className={cn(
          "mt-0 flex max-h-[calc(100dvh-max(env(safe-area-inset-top),12px))] flex-col overflow-hidden border-white/[0.08] bg-background outline-none",
          "md:max-lg:mx-auto md:max-lg:w-full md:max-lg:max-w-[640px]",
          full && "h-[calc(100dvh-max(env(safe-area-inset-top),12px))]",
          className
        )}
      >
        {header ?? (
          <div className="flex shrink-0 items-center gap-2 pl-4 pr-2 pt-1 pb-2">
            <div className="min-w-0 flex-1">
              <DrawerTitle className="truncate text-[1.0625rem] font-semibold text-white/90">
                {title}
              </DrawerTitle>
              {description ? (
                <DrawerDescription className="mt-0.5 truncate text-[0.8125rem] text-faint">
                  {description}
                </DrawerDescription>
              ) : null}
            </div>
            <IconButton
              label="Close"
              onClick={() => onOpenChange(false)}
              className="h-11 w-11"
            >
              <XIcon className="size-5" />
            </IconButton>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-white/[0.06] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        ) : (
          <div className="shrink-0 pb-[env(safe-area-inset-bottom)]" />
        )}
      </DrawerContent>
    </Drawer>
  );
};
